/**
 * Visual Effects (VFX) — cinematic one-shot effects for story beats.
 *
 * Each effect is a fire-and-forget animation applied to the stage element.
 * Effects are implemented via CSS classes + requestAnimationFrame and resolve
 * when the animation completes. They do NOT block the narrative — they play
 * concurrently with typewriter text reveal.
 *
 * Usage:
 *   import { playVfx } from './vfx';
 *   playVfx('shake', stageEl);
 */

import type { VfxKind } from '../script/schema';

export type { VfxKind };

// Duration map — how long each effect class stays on the element (ms)
const VFX_DURATION: Record<VfxKind, number> = {
  'shake':          500,
  'glitch':         800,
  'glitch-heavy':  1400,
  'flash-white':    600,
  'flash-red':      500,
  'vignette':      2000,
  'desaturate':    3000,
  'bloom-warm':    2500,
  'data-cascade':  3000,
  'darkness-creep':3500,
};

/**
 * Play a named visual effect on the stage element.
 * Returns a promise that resolves when the effect finishes.
 */
export function playVfx(kind: VfxKind, stage: HTMLElement): Promise<void> {
  return new Promise((resolve) => {
    const className = `vfx-${kind}`;

    // For overlay-style effects we inject a dedicated div so we don't
    // fight with the stage's existing classes.
    const needsOverlay = (
      kind === 'flash-white' ||
      kind === 'flash-red' ||
      kind === 'data-cascade' ||
      kind === 'darkness-creep' ||
      kind === 'bloom-warm'
    );

    if (needsOverlay) {
      const overlay = document.createElement('div');
      overlay.className = `vfx-overlay ${className}`;
      stage.appendChild(overlay);

      const duration = VFX_DURATION[kind];
      setTimeout(() => {
        overlay.remove();
        resolve();
      }, duration);
    } else {
      // Class-on-stage effects
      stage.classList.add(className);
      const duration = VFX_DURATION[kind];
      setTimeout(() => {
        stage.classList.remove(className);
        resolve();
      }, duration);
    }
  });
}

/**
 * Play multiple effects concurrently.
 */
export function playVfxAll(kinds: VfxKind[], stage: HTMLElement): Promise<void> {
  return Promise.all(kinds.map((k) => playVfx(k, stage))).then(() => undefined);
}
